import { Component, type ReactElement } from "react";

import { HStack } from "widgets";

import { ContentPlaceholder } from "../ContentPlaceholder.js";
import { AsyncSearchWithExprParser } from "./AsyncSearchWithExprParser.js";
import { SelectAllNoneWidget } from "./SelectWidget.js";

// In order to support different item types to be sorted (by drag/drop) and selected, we are using Typescript Generics here.
// Therefore, all item-specific type definitions are using the type variable <T>.
// The actual specific type is defined just before usage of the component and evaluated at compile time.
export interface IGenericSortSelectionFormProps<T> {
  items: T[];

  renderItem: (index: number, item: T) => ReactElement;
  onFilter: (filterTerm: string, item: T) => boolean;
  handleSelection?: (items: T[], select: boolean) => void;
  columns?: number;
  tooltip?: string;
}

export interface IGenericSortSelectionFormState<T> {
  filteredItems?: T[];
  filterTerm?: string;
}

export class GenericSortSelectionForm<T> extends Component<
  IGenericSortSelectionFormProps<T>,
  IGenericSortSelectionFormState<T>
> {
  constructor(props: IGenericSortSelectionFormProps<T>) {
    super(props);

    this.state = {
      filteredItems: props.items,
      filterTerm: "",
    };
  }

  componentDidUpdate(
    prevProps: Readonly<IGenericSortSelectionFormProps<T>>,
    prevState: Readonly<IGenericSortSelectionFormState<T>>,
  ) {
    if (this.props.items !== prevProps.items || this.state.filterTerm !== prevState.filterTerm) {
      this.filterItems();
    }
  }

  filterItems() {
    const items: T[] = this.props.items;
    const filteredItems: T[] = this.state.filterTerm
      ? items.filter((item: T) => this.props.onFilter(this.state.filterTerm, item))
      : items;
    this.setState({ filteredItems });
  }

  onFilterChange = (filterTerm: string, _terms: string[]) => {
    return new Promise<void>((resolve) => {
      // filtering is case insensitive
      this.setState({ filterTerm: filterTerm });
      resolve();
    });
  };

  splitColumnList(items: ReactElement[], columnNumber: number): ReactElement[][] {
    const minRowsPerCol = 3;

    let rowsPerCol: number;
    if (items.length <= minRowsPerCol * columnNumber) {
      rowsPerCol = minRowsPerCol;
    } else {
      rowsPerCol = Math.floor((items.length - 1) / columnNumber) + 1;
    }

    const listOfLists: any[] = [];
    let myList: any[] = [];
    let cnt = 0;
    for (let i = 0; i < items.length; i++) {
      if (i === 0 || cnt === rowsPerCol) {
        if (i !== 0) {
          listOfLists.push(myList);
        }
        myList = [];
        cnt = 0;
      }
      myList.push(items[i]);
      cnt++;
    }
    listOfLists.push(myList);

    return listOfLists;
  }

  render() {
    const columnNumber = this.props.columns || 2;
    const filteredItems: ReactElement[] = this.state.filteredItems
      .map((item: T, index: number) => this.props.renderItem(index, item))
      .filter((el) => !!el);

    // let list wrap in case of lots of entries
    const listOfLists: any[] = this.splitColumnList(filteredItems, columnNumber);

    const columns: ReactElement[] = [];
    for (let i = 0; i < columnNumber; i++) {
      columns.push(
        <div key={`sort-selection-form-col-${i}`} style={{ flex: 1 }}>
          {listOfLists[i]}
        </div>,
      );
    }

    return (
      <div className="generic-sort-selection-modal">
        <div style={{ paddingBottom: "20px", display: "inline-block", width: "100%" }}>
          <div style={{ float: "left" }}>
            <AsyncSearchWithExprParser
              onSearchExprChange={this.onFilterChange}
              controlledValue={this.state.filterTerm}
            />
          </div>
          <div style={{ float: "right" }} title={this.props.tooltip}>
            {this.props.handleSelection && (
              <div style={{ paddingLeft: 20, float: "left" }}>
                <SelectAllNoneWidget
                  current={-1}
                  max={1}
                  onSelectAll={(all) => this.props.handleSelection(this.state.filteredItems, all)}
                />
              </div>
            )}
          </div>
        </div>

        {filteredItems.length === 0 && (
          <ContentPlaceholder
            text={this.props.items.length === 0 ? "No item to show" : "No item corresponds to your filter term"}
          />
        )}
        <HStack style={{ width: "100%" }} spacing={20}>
          {columns}
        </HStack>
      </div>
    );
  }
}
